import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Card,
  CardContent,
  Typography,
  Box,
  Stack,
  Button,
  Divider,
  Grid,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Chip,
} from '@mui/material';
import { LoadingButton } from '@mui/lab';
import { toast } from 'react-toastify';
import { format } from 'date-fns';
import { useSelector } from 'react-redux';
import Iconify from '../../../components/iconify';
import BreadCrumb from '../../../components/breadcrumb/BreadCrumbNavigation';
import WfhStatusBadge from './WfhStatusBadge';
import { WFH_STATUS, WFH_HALF_DAY_TYPE_LABELS } from '../constants';
import { getWfhById, updateWfhStatus, cancelWfhRequest, cancelApprovedWfh } from '../../../services/wfhService';
import { isAdmin, isManager, isHR, checkComponentPermission } from '../../../utils/share';
import Loading from '../../../layouts/loading';
import { useStyles } from '../styles/WfhDetails';

const WfhDetails = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const user = useSelector((state) => state?.login?.user || null);
  const userId = user?.id;
  const [wfh, setWfh] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [approveDialogOpen, setApproveDialogOpen] = useState(false);
  const [rejectDialogOpen, setRejectDialogOpen] = useState(false);
  const [cancelDialogOpen, setCancelDialogOpen] = useState(false);
  const [cancelApprovedDialogOpen, setCancelApprovedDialogOpen] = useState(false);
  const [rejectionReason, setRejectionReason] = useState('');
  const [cancellationReason, setCancellationReason] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const classes = useStyles();

  const isOwnWfh = wfh?.userId === userId;
  const canApproveReject =
    (isAdmin() || ((isManager() || isHR()) && !isOwnWfh)) &&
    checkComponentPermission('APPROVE_WFH') &&
    wfh?.status === WFH_STATUS.PENDING;
  const canCancel = isOwnWfh && wfh?.status === WFH_STATUS.PENDING && checkComponentPermission('DELETE_WFH');
  const canCancelApproved =
    (isAdmin() || isManager() || isHR()) &&
    checkComponentPermission('APPROVE_WFH') &&
    wfh?.status === WFH_STATUS.APPROVED;

  const fetchWfhDetails = async () => {
    setIsLoading(true);
    try {
      const response = await getWfhById(id);
      setWfh(response?.data?.data);
    } catch (error) {
      toast.error(error?.response?.data?.message || 'Error fetching WFH details');
      navigate('/wfh');
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchWfhDetails();
  }, [id]);

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    try {
      return format(new Date(dateString), 'dd-MMM-yyyy');
    } catch (error) {
      return dateString;
    }
  };

  const getHalfDayLabel = () => {
    if (!wfh?.isHalfDay) return 'Full Day';
    return WFH_HALF_DAY_TYPE_LABELS[wfh.halfDayType] || wfh.halfDayType;
  };

  const handleApprove = async () => {
    setIsSubmitting(true);
    try {
      await updateWfhStatus(id, { status: 'approved' });
      toast.success('WFH request approved successfully');
      setApproveDialogOpen(false);
      fetchWfhDetails();
    } catch (error) {
      toast.error(error?.response?.data?.message || 'Error approving WFH request');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleReject = async () => {
    if (!rejectionReason.trim()) {
      toast.error('Please provide a reason for rejection');
      return;
    }
    setIsSubmitting(true);
    try {
      await updateWfhStatus(id, { status: 'rejected', rejectionReason: rejectionReason.trim() });
      toast.success('WFH request rejected successfully');
      setRejectDialogOpen(false);
      setRejectionReason('');
      fetchWfhDetails();
    } catch (error) {
      toast.error(error?.response?.data?.message || 'Error rejecting WFH request');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCancel = async () => {
    setIsSubmitting(true);
    try {
      await cancelWfhRequest(id);
      toast.success('WFH request deleted successfully');
      setCancelDialogOpen(false);
      navigate('/wfh');
    } catch (error) {
      toast.error(error?.response?.data?.message || 'Error deleting WFH request');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCancelApproved = async () => {
    setIsSubmitting(true);
    try {
      await cancelApprovedWfh(id, { cancellationReason: cancellationReason.trim() || undefined });
      toast.success('WFH request cancelled successfully');
      setCancelApprovedDialogOpen(false);
      setCancellationReason('');
      fetchWfhDetails();
    } catch (error) {
      toast.error(error?.response?.data?.message || 'Error cancelling WFH request');
    } finally {
      setIsSubmitting(false);
    }
  };

  const breadcrumbs = [
    { label: 'Home', path: '/' },
    { label: 'WFH', path: '/wfh' },
    { label: 'WFH Details', path: '' },
  ];

  if (isLoading) {
    return <Loading open={isLoading} />;
  }

  if (!wfh) {
    return null;
  }

  return (
    <>
      <BreadCrumb breadcrumbs={breadcrumbs} />
      <Box sx={{ maxWidth: 1000, margin: 'auto', padding: 3 }}>
        <Stack direction="row" alignItems="center" justifyContent="space-between" mb={3}>
          <Typography variant="h4">WFH Request Details</Typography>
          <Button
            variant="outlined"
            startIcon={<Iconify icon="eva:arrow-back-fill" />}
            onClick={() => navigate('/wfh')}
          >
            Back to WFH
          </Button>
        </Stack>

        <Card className={classes.wfhDetailsCard}>
          <CardContent>
            <Stack spacing={1.5}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Typography variant="h6">WFH Information</Typography>
                <WfhStatusBadge status={wfh.status} />
              </Box>

              <Divider />

              <Grid container spacing={2} gap={1}>
                <Grid item xs={12} md={2.5} className={classes.detailIndCard}>
                  <Typography variant="body2" color="text.secondary">
                    Employee Name
                  </Typography>
                  <Typography variant="body1" sx={{ mt: 0.5, fontWeight: 500 }}>
                    {wfh.employee?.userName || wfh.employee?.email || '-'}
                  </Typography>
                </Grid>

                <Grid item xs={12} md={2.5} className={classes.detailIndCard}>
                  <Typography variant="body2" color="text.secondary">
                    From Date
                  </Typography>
                  <Typography variant="body1" sx={{ mt: 0.5, fontWeight: 500 }}>
                    {formatDate(wfh.startDate)}
                  </Typography>
                </Grid>

                <Grid item xs={12} md={2.5} className={classes.detailIndCard}>
                  <Typography variant="body2" color="text.secondary">
                    To Date
                  </Typography>
                  <Typography variant="body1" sx={{ mt: 0.5, fontWeight: 500 }}>
                    {formatDate(wfh.endDate)}
                  </Typography>
                </Grid>

                <Grid item xs={12} md={2.5} className={classes.detailIndCard}>
                  <Typography variant="body2" color="text.secondary">
                    Total Days
                  </Typography>
                  <Typography variant="body1" sx={{ mt: 0.5, fontWeight: 500 }}>
                    {wfh.totalDays} {wfh.totalDays === 1 ? 'day' : 'days'}
                  </Typography>
                </Grid>

                <Grid item xs={12} md={2.5} className={classes.detailIndCard}>
                  <Typography variant="body2" color="text.secondary">
                    WFH Type
                  </Typography>
                  <Typography variant="body1" sx={{ mt: 0.5, fontWeight: 500 }}>
                    {getHalfDayLabel()}
                  </Typography>
                </Grid>

                <Grid item xs={12} md={2.5} className={classes.detailIndCard}>
                  <Typography variant="body2" color="text.secondary">
                    Requested Date
                  </Typography>
                  <Typography variant="body1" sx={{ mt: 0.5, fontWeight: 500 }}>
                    {formatDate(wfh.createdAt)}
                  </Typography>
                </Grid>

                {wfh.reason && (
                  <Grid item xs={12} className={classes.detailIndCard}>
                    <Typography variant="body2" color="text.secondary">
                      Reason
                    </Typography>
                    <Typography variant="body1" sx={{ mt: 0.5, whiteSpace: 'pre-wrap' }}>
                      {wfh.reason}
                    </Typography>
                  </Grid>
                )}

                {wfh.reviewers && Array.isArray(wfh.reviewers) && wfh.reviewers.length > 0 && (
                  <Grid item xs={12} className={classes.detailIndCard}>
                    <Typography variant="body2" color="text.secondary">
                      Selected Reviewers
                    </Typography>
                    <Box sx={{ mt: 0.5, display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                      {wfh.reviewerDetails && wfh.reviewerDetails.length > 0
                        ? wfh.reviewerDetails.map((reviewer) => (
                            <Chip
                              key={reviewer.id}
                              label={reviewer.userName || reviewer.email || `User ${reviewer.id}`}
                              size="small"
                              color="primary"
                              variant="outlined"
                            />
                          ))
                        : wfh.reviewers.map((reviewerId) => (
                            <Chip
                              key={reviewerId}
                              label={`User ${reviewerId}`}
                              size="small"
                              color="primary"
                              variant="outlined"
                            />
                          ))}
                    </Box>
                    <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
                      These reviewers have been notified via email
                    </Typography>
                  </Grid>
                )}

                {wfh.reviewedBy && (
                  <>
                    <Grid item xs={12} md={6} className={classes.detailIndCard}>
                      <Typography variant="body2" color="text.secondary">
                        Reviewed By
                      </Typography>
                      <Typography variant="body1" sx={{ mt: 0.5, fontWeight: 500 }}>
                        {wfh.approver && wfh.approver.id === userId
                          ? 'Me'
                          : wfh.approver?.userName || wfh.approver?.email || '-'}
                      </Typography>
                    </Grid>

                    <Grid item xs={12} md={6} className={classes.detailIndCard}>
                      <Typography variant="body2" color="text.secondary">
                        Reviewed Date
                      </Typography>
                      <Typography variant="body1" sx={{ mt: 0.5, fontWeight: 500 }}>
                        {formatDate(wfh.reviewedAt)}
                      </Typography>
                    </Grid>
                  </>
                )}

                {wfh.rejectionReason && (
                  <Grid item xs={12} className={classes.detailIndCard}>
                    <Typography variant="body2" color="text.secondary">
                      Rejection / Cancellation Reason
                    </Typography>
                    <Typography variant="body1" sx={{ mt: 0.5, color: 'error.main', whiteSpace: 'pre-wrap' }}>
                      {wfh.rejectionReason}
                    </Typography>
                  </Grid>
                )}
              </Grid>

              {(canApproveReject || canCancel || canCancelApproved) && (
                <>
                  <Divider />
                  <Stack direction="row" spacing={2} justifyContent="flex-end">
                    {canCancel && (
                      <Button
                        variant="outlined"
                        color="error"
                        startIcon={<Iconify icon="material-symbols:delete" />}
                        onClick={() => setCancelDialogOpen(true)}
                      >
                        Delete WFH
                      </Button>
                    )}
                    {canCancelApproved && (
                      <Button
                        variant="outlined"
                        color="warning"
                        startIcon={<Iconify icon="eva:close-circle-fill" />}
                        onClick={() => setCancelApprovedDialogOpen(true)}
                      >
                        Cancel WFH
                      </Button>
                    )}
                    {canApproveReject && (
                      <>
                        <Button
                          variant="outlined"
                          color="error"
                          startIcon={<Iconify icon="eva:close-circle-fill" />}
                          onClick={() => setRejectDialogOpen(true)}
                        >
                          Reject
                        </Button>
                        <Button
                          variant="contained"
                          color="success"
                          startIcon={<Iconify icon="eva:checkmark-circle-2-fill" />}
                          onClick={() => setApproveDialogOpen(true)}
                        >
                          Approve
                        </Button>
                      </>
                    )}
                  </Stack>
                </>
              )}
            </Stack>
          </CardContent>
        </Card>
      </Box>

      <Dialog open={approveDialogOpen} onClose={() => setApproveDialogOpen(false)}>
        <DialogTitle>Approve WFH Request</DialogTitle>
        <DialogContent>
          <Typography>Are you sure you want to approve this WFH request?</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setApproveDialogOpen(false)}>Cancel</Button>
          <LoadingButton variant="contained" color="success" onClick={handleApprove} loading={isSubmitting}>
            Approve
          </LoadingButton>
        </DialogActions>
      </Dialog>

      <Dialog open={rejectDialogOpen} onClose={() => setRejectDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Reject WFH Request</DialogTitle>
        <DialogContent>
          <Typography sx={{ mb: 2 }}>Please provide a reason for rejecting this WFH request:</Typography>
          <TextField
            autoFocus
            multiline
            rows={4}
            fullWidth
            label="Rejection Reason"
            value={rejectionReason}
            onChange={(e) => setRejectionReason(e.target.value)}
            placeholder="Enter the reason for rejection..."
            required
            error={!rejectionReason.trim() && rejectionReason.length > 0}
            helperText={!rejectionReason.trim() && rejectionReason.length > 0 ? 'Rejection reason is required' : ''}
          />
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              setRejectDialogOpen(false);
              setRejectionReason('');
            }}
          >
            Cancel
          </Button>
          <LoadingButton
            variant="contained"
            color="error"
            onClick={handleReject}
            loading={isSubmitting}
            disabled={!rejectionReason.trim()}
          >
            Reject
          </LoadingButton>
        </DialogActions>
      </Dialog>

      <Dialog open={cancelDialogOpen} onClose={() => setCancelDialogOpen(false)}>
        <DialogTitle>Delete WFH Request</DialogTitle>
        <DialogContent>
          <Typography>Are you sure you want to delete this WFH request? This action cannot be undone.</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCancelDialogOpen(false)}>No</Button>
          <LoadingButton variant="contained" color="error" onClick={handleCancel} loading={isSubmitting}>
            Yes, Delete
          </LoadingButton>
        </DialogActions>
      </Dialog>

      <Dialog
        open={cancelApprovedDialogOpen}
        onClose={() => {
          setCancelApprovedDialogOpen(false);
          setCancellationReason('');
        }}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Cancel Approved WFH</DialogTitle>
        <DialogContent>
          <Typography sx={{ mb: 2 }}>
            Are you sure you want to cancel this approved WFH request? The employee will be notified.
          </Typography>
          <TextField
            autoFocus
            multiline
            rows={3}
            fullWidth
            label="Cancellation Reason (optional)"
            value={cancellationReason}
            onChange={(e) => setCancellationReason(e.target.value)}
            placeholder="Enter the reason for cancellation..."
          />
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              setCancelApprovedDialogOpen(false);
              setCancellationReason('');
            }}
          >
            No
          </Button>
          <LoadingButton variant="contained" color="warning" onClick={handleCancelApproved} loading={isSubmitting}>
            Yes, Cancel WFH
          </LoadingButton>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default WfhDetails;
